'use client';

import React, { useState } from 'react';

// 5x5 Risk Matrix: Likelihood (1-5) × Severity (1-5)
// Risk Score = Likelihood × Severity
// 1-4: Low, 5-9: Medium, 10-14: High, 15-19: Critical, 20-25: Extreme

const riskItems = [
  { id: 'risk-001', likelihood: 4, severity: 5, label: 'Chemical release — storage failure', dept: 'Chemical Proc.' },
  { id: 'risk-002', likelihood: 3, severity: 4, label: 'Confined space atmospheric hazard', dept: 'Maintenance' },
  { id: 'risk-003', likelihood: 2, severity: 5, label: 'Major process fire / explosion', dept: 'Operations' },
  { id: 'risk-004', likelihood: 4, severity: 3, label: 'Repetitive strain injury surge', dept: 'Maintenance' },
  { id: 'risk-005', likelihood: 3, severity: 3, label: 'Forklift pedestrian collision', dept: 'Logistics' },
  { id: 'risk-006', likelihood: 2, severity: 3, label: 'Electrical arc flash', dept: 'Electrical' },
  { id: 'risk-007', likelihood: 5, severity: 2, label: 'Slip/fall on wet surfaces', dept: 'Operations' },
  { id: 'risk-008', likelihood: 1, severity: 5, label: 'Catastrophic structural failure', dept: 'Construction' },
  { id: 'risk-009', likelihood: 3, severity: 2, label: 'Noise-induced hearing loss', dept: 'Chemical Proc.' },
  { id: 'risk-010', likelihood: 4, severity: 2, label: 'Manual handling injury', dept: 'Logistics' },
];

const cellColor = (l: number, s: number) => {
  const score = l * s;
  if (score >= 20) return 'bg-red-600/80';
  if (score >= 15) return 'bg-red-500/60';
  if (score >= 10) return 'bg-orange-500/60';
  if (score >= 5) return 'bg-amber-500/60';
  return 'bg-emerald-500/50';
};

const cellLabel = (l: number, s: number) => {
  const score = l * s;
  if (score >= 20) return 'Extreme';
  if (score >= 15) return 'Critical';
  if (score >= 10) return 'High';
  if (score >= 5) return 'Medium';
  return 'Low';
};

export default function RiskMatrix() {
  const [hovered, setHovered] = useState<string | null>(null);

  const getItemsInCell = (l: number, s: number) =>
    riskItems.filter((r) => r.likelihood === l && r.severity === s);

  return (
    <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-5">
      <div className="mb-4">
        <h3 className="text-sm font-600 text-[hsl(var(--foreground))]">Risk Assessment Matrix</h3>
        <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
          Likelihood × Severity · {riskItems.length} active risks plotted
        </p>
      </div>

      <div className="flex gap-2">
        {/* Y axis label */}
        <div className="flex items-center justify-center" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
          <span className="text-[10px] font-600 uppercase tracking-widest text-[hsl(var(--muted-foreground))]">
            Likelihood →
          </span>
        </div>

        <div className="flex-1">
          {/* Matrix grid */}
          <div className="grid gap-1" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
            {[5, 4, 3, 2, 1].map((l) =>
              [1, 2, 3, 4, 5].map((s) => {
                const items = getItemsInCell(l, s);
                return (
                  <div
                    key={`cell-${l}-${s}`}
                    className={`
                      relative rounded-md aspect-square flex flex-col items-center justify-center
                      ${cellColor(l, s)} transition-all duration-150 cursor-default
                      border border-white/10
                    `}
                  >
                    <span className="text-[9px] font-600 text-white/70 tabular-nums">{l * s}</span>
                    {items.length > 0 && (
                      <div className="flex flex-wrap gap-0.5 justify-center mt-0.5">
                        {items.map((item) => (
                          <div
                            key={`dot-${item.id}`}
                            className="w-2.5 h-2.5 rounded-full bg-white/90 shadow cursor-pointer relative group"
                            onMouseEnter={() => setHovered(item.id)}
                            onMouseLeave={() => setHovered(null)}
                          >
                            {hovered === item.id && (
                              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 z-30 w-44 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-lg px-3 py-2 shadow-xl pointer-events-none animate-fade-in">
                                <p className="text-[11px] font-600 text-[hsl(var(--foreground))] leading-snug mb-1">
                                  {item.label}
                                </p>
                                <p className="text-[10px] text-[hsl(var(--muted-foreground))]">{item.dept}</p>
                                <p className="text-[10px] font-600 text-amber-500 mt-0.5">
                                  Score: {item.likelihood * item.severity} · {cellLabel(item.likelihood, item.severity)}
                                </p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* X axis label */}
          <div className="flex justify-center mt-1.5">
            <span className="text-[10px] font-600 uppercase tracking-widest text-[hsl(var(--muted-foreground))]">
              Severity →
            </span>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 mt-4 flex-wrap">
        {[
          { id: 'leg-low', label: 'Low (1–4)', color: 'bg-emerald-500/50' },
          { id: 'leg-med', label: 'Medium (5–9)', color: 'bg-amber-500/60' },
          { id: 'leg-high', label: 'High (10–14)', color: 'bg-orange-500/60' },
          { id: 'leg-crit', label: 'Critical (15–19)', color: 'bg-red-500/60' },
          { id: 'leg-ext', label: 'Extreme (20–25)', color: 'bg-red-600/80' },
        ].map((item) => (
          <div key={item.id} className="flex items-center gap-1.5">
            <span className={`w-3 h-3 rounded-sm ${item.color}`} />
            <span className="text-[10px] text-[hsl(var(--muted-foreground))]">{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}